<template>
  <div class="create-request-modal">
    <b-modal
      :id="idModal"
      ref="bModal"
      qa="clinical-search-patient-modal"
      size="full-screen"
      modal-class="create-request-modal"
      centered
      @ok="onOk"
      @hide="onHide"
      @shown="focusIdentifier"
    >
      <template #modal-title>
        <i
          class="fal fa-file-medical mr-2"
          aria-hidden="true"
        />{{ isReassign ? $t('clinical.btn.reassignPatient') : $t('component.patient.searcher.createRequest') }}
      </template>
      <b-container
        fluid
        class="p-2"
      >
        <b-row
          v-if="!isReassign"
          class="d-flex justify-content-between"
        >
          <b-col>
            <span class="dark">{{ $t('component.patient.searcher.requestDescription') }}:</span>
          </b-col>
        </b-row>
        <div
          class="search-box float-component px-3 my-3"
          :class="{'search-done': searchDone}"
        >
          <b-form>
            <b-row>
              <b-col
                class="px-1 d-flex align-items-center"
                cols="1"
              >
                <div class="select-section identifiers">
                  <vt-combo-select
                    v-model="identifierSelected"
                    track-by="code"
                    class="nowrap id-model-select"
                    qa="clinical-search-patient-modal-patient-identifier-select"
                    :options="patientIdentifierOptions"
                    label="name"
                  />
                </div>
              </b-col>
              <b-col
                v-if="identifierSelected && identifierSelected.code === centerIdentifierCode"
                class="px-1 d-flex align-items-center"
              >
                <div class="select-section">
                  <vt-combo-select
                    v-model="centerSelected"
                    track-by="code"
                    qa="clinical-search-patient-modal-center-select"
                    :options="centers"
                    label="name"
                    :placeholder="$t('global.center')"
                  />
                </div>
              </b-col>
              <b-col class="px-1">
                <div class="input-section">
                  <vt-input
                    ref="patientIdentifier"
                    v-model="id"
                    qa="clinical-search-patient-modal-identifier-input"
                    :placeholder="$tc('global.identifier', 1)"
                    :name="$tc('global.identifier', 1)"
                    class="form-control-sm"
                    type="text"
                    autofocus
                  />
                </div>
              </b-col>
              <b-col class="px-1">
                <div class="input-section">
                  <vt-input
                    v-model="surname1"
                    qa="clinical-search-patient-modal-surname1-input"
                    :placeholder="$t('global.surname1')"
                    :name="$t('global.surname1')"
                    class="form-control-sm"
                    type="text"
                  />
                </div>
              </b-col>
              <b-col
                v-if="globalParameters.secondSurnameSupported"
                class="px-1"
              >
                <div class="input-section">
                  <vt-input
                    v-model="surname2"
                    qa="clinical-search-patient-modal-suername2-input"
                    :placeholder="$t('global.surname2')"
                    :name="$t('global.surname2')"
                    class="form-control-sm"
                    type="text"
                  />
                </div>
              </b-col>
              <b-col class="px-1">
                <div class="input-section">
                  <vt-input
                    v-model="name"
                    qa="clinical-search-patient-modal-name-input"
                    :placeholder="$tc('global.name', 1)"
                    :name="$tc('global.name', 1)"
                    class="form-control-sm"
                    type="text"
                  />
                </div>
              </b-col>
              <b-col
                v-if="globalParameters.middleNameSupported"
                class="px-1"
              >
                <div class="input-section">
                  <vt-input
                    v-model="middleName"
                    qa="clinical-search-patient-modal-middlename-input"
                    :placeholder="$t('global.middleName')"
                    :name="$t('global.middleName')"
                    class="form-control-sm"
                    type="text"
                  />
                </div>
              </b-col>
              <b-col
                class="text-center icon-column d-flex align-items-center justify-content-end px-1"
                cols="auto"
              >
                <div class="d-flex align-items-center justify-content-end">
                  <b-button
                    v-if="searchDone"
                    qa="clinical-search-patient-modal-clear-button"
                    class="btn__icon btn__icon--primary  alt-btn px-1"
                    @click="clearSearch"
                  >
                    <i
                      class="far fa-times"
                      aria-hidden="true"
                    />
                  </b-button>
                  <vt-loading-button
                    qa="clinical-search-patient-modal-search-button"
                    class="btn__icon btn__icon--primary px-2"
                    :params="{ promise: searchPatients, shortkeys: ['enter'] }"
                  >
                    <i
                      class="fas fa-search"
                      aria-hidden="true"
                    />
                  </vt-loading-button>
                </div>
              </b-col>
            </b-row>
          </b-form>
        </div>
      </b-container>
      <div>
        <hr style="margin:0">
      </div>
      <div
        v-if="loading"
        class="loading-state"
      >
        <vt-loading />
      </div>
      <div
        v-if="!loading"
        class="patient-container justify-content-center"
      >
        <div
          v-if="!rowData.length > 0 && !noResults && !tooManyResults"
          class="empty-search d-flex align-items-center justify-content-center p-4"
        >
          <div class="w-100">
            <b-row>
              <b-col>
                <div class="d-flex flex-column text-center">
                  <div>
                    <i
                      class="fal fa-th-list fa-2x mb-2"
                      aria-hidden="true"
                    />
                    <i
                      class="far fa-search ml-1"
                      aria-hidden="true"
                    />
                  </div>
                  <span style="font-size: 16px; margin-top: 16px;">{{ $t('component.patient.searcher.emptyStateText') }}</span>
                </div>
              </b-col>
            </b-row>
          </div>
        </div>
        <div
          v-if="tooManyResults"
          class="empty-search d-flex align-items-center justify-content-center p-4"
        >
          <div class="w-100">
            <b-row>
              <b-col>
                <div class="d-flex flex-column text-center">
                  <div>
                    <i
                      class="error fal fa-exclamation-circle fa-2x mb-2"
                      aria-hidden="true"
                    />
                  </div>
                  <span
                    class="error"
                    style="font-size: 16px; margin-top: 16px;"
                  >{{ $t('component.patient.searcher.tooManySearchResults') }}</span>
                </div>
              </b-col>
            </b-row>
          </div>
        </div>
        <div
          v-if="noResults"
          class="empty-search d-flex align-items-center justify-content-center p-4"
        >
          <div class="w-100">
            <b-row>
              <b-col>
                <div class="d-flex flex-column text-center">
                  <div>
                    <i
                      class="error fal fa-exclamation-circle fa-2x mb-2"
                      aria-hidden="true"
                    />
                  </div>
                  <span
                    class="error"
                    style="font-size: 16px; margin-top: 16px;"
                  >{{ $t('component.patient.searcher.noResults') }}</span>
                </div>
              </b-col>
            </b-row>
          </div>
        </div>
        <div>
          <vt-grid
            v-if="rowData.length > 0"
            qa="clinical-search-patient-modal-grid"
            class="ps-grid patient mt-3"
            :grid-options="gridOptions"
            :column-defs="columnDefs"
            :row-data="rowData"
            master-detail
            use-detail-custom-config
            :detail-cell-renderer-params="detailCellRendererParams"
            component-code="uicomponent.petitioner.patientgrid"
            pagination
          />
        </div>
      </div>
      <div
        slot="modal-footer"
        class="footer-banner pr-2 pl-2"
        style="width:100%;"
      >
        <div class="row">
          <div class="col text-left">
            <b-button
              qa="clinical-search-patient-modal-cancel"
              size="sm"
              variant="outline-secondary"
              data-dismiss="modal"
              @click="hide"
            >
              {{ $t('global.cancel') }}
            </b-button>
          </div>
          <div
            v-if="rowData.length > 0"
            class="col-10 text-right"
          >
            <b-button
              qa="clinical-search-patient-modal-create-request"
              size="sm"
              class="btn--primary"
              :disabled="selectedRows.length !== 1"
              @click="createRequest(selectedRows[0].patId)"
            >
              <i
                class="far fa-file-medical mr-2"
                aria-hidden="true"
              />
              {{ isReassign ? $t('clinical.btn.reassignPatient') : $t('component.patient.searcher.createRequest') }}
            </b-button>
          </div>
        </div>
      </div>
    </b-modal>
    <modal-patient-card
      v-if="patientIdSelected"
      ref="modalPatientCard"
      :patient-id="patientIdSelected"
    />
  </div>
</template>
<script>
import sessionStore from '@/session/sessionStore'
import gridSideBarConfig from '@/grid/gridSideBarConfig'
import VtGrid from '@/components/VtGrid/VtGrid'
import patientResource from '@/api/resources/PatientResource'
import { RequestOperation } from '@/api/operations/models/request-operation'
import { PatientOperation } from '@/api/operations/models/patient-operation'
import { REQUEST_STATES } from '@/api/resources/entityState/request-state'
import clinicalRequestService from '@/modules/clinicalModule/services/clinicalRequestService'
import Encoder from '@/utils/encoder'
import PatientIdentifierResource from '@/api/resources/PatientIdentifierResource'
import CenterResource from '@/api/resources/common/commonCenterService'
import ModalPatientCard from '@/components/modals/ModalPatientCard'
import { PAGINATOR_BIG_PAGE_SIZE } from '@/shared/services/services-constants'
import { clinicalSearchPatientModalGridMasterColumnDefs } from '@/modules/clinicalModule/components/clinical-search-patient-modal/column-defs/clinical-search-patient-modal-grid-master.column-defs'
import { clinicalSearchPatientModalGridDetailColumnDefs } from '@/modules/clinicalModule/components/clinical-search-patient-modal/column-defs/clinical-search-patient-modal-grid-detail.column-defs'
import i18n from '@/lang/i18n'
import { AgGridRowModelType } from '@/shared/models/ag-grid.model'
import { HelperUtils } from '@/shared/util/helper-utils'

export default {
    name: 'ClinicalSearchPatientModal',
    components: { VtGrid, ModalPatientCard },
    props: {
        idModal: { type: String, required: true },
        actionButton: {
            type: String,
            default: () => i18n.tc('component.patient.searcher.createRequest')
        },
        titleModal: {
            type: String,
            default: () => i18n.tc('global.patients')
        },
        canCreateRequest: { type: Boolean, default: false },
        canSelectPatient: { type: Boolean, default: false }
    },
    data() {
        return {
            isReassign: false,
            patientIdSelected: null,
            centerIdentifierCode: 'identifierType.NHC',
            encoder: new Encoder(),
            patientOperation: PatientOperation,
            requestOperation: RequestOperation,
            isModalOpen: false,
            isHidden: true,
            selectedRows: [],
            sessionState: sessionStore.state,
            id: null,
            surname1: null,
            surname2: null,
            middleName: null,
            name: null,
            tooManyResults: false,
            loading: false,
            noResults: false,
            selectedPatient: null,
            selectedCaseId: null,
            selectedStudyRequest: null,
            gridApi: null,
            rowData: [],
            coreEntity: 'PATIENT',
            detailCellRendererParams: {},
            centerSelected: {},
            patientIdentifierOptions: [],
            centers: [],
            identifierSelected: null,
            searchDone: false,
            columnDefs: []
        }
    },
    computed: {
        regex() {
            return this.$store.getters.regex.filter(
                (regex) => this.identifierSelected.code === regex.identifierType.code)
        },
        laboratoryId() {
            return this.$store.getters.labId
        },
        gridOptions() {
            return {
                pagination: false,
                rowModelType: AgGridRowModelType.CLIENT_SIDE,
                getContextMenuItems: this.getContextMenuItems,
                floatingFilter: false,
                sideBar: gridSideBarConfig,
                onRowDoubleClicked: this.onRowClicked,
                onGridReady: this.onGridReady,
                onRowDataChanged: this.onRowDataChanged,
                onSelectionChanged: this.onSelectionChanged
            }
        }
    },
    methods: {
        onRowDataChanged({ api }) {
            if (this.rowData.length === 1) {
                const singleRow = this.rowData[0]
                // TODO Check single row id prop
                const singleRowIdProp = 'patId'
                const singleRowId = singleRow.patId
                api.forEachNode(node => {
                    if (node.data[singleRowIdProp] === singleRowId) {
                        node.setSelected(true)
                    }
                })
            }
        },
        async addPatientIdentifiersColumns() {
            const response =
                await this.$_toCallSecure(
                    PatientIdentifierResource.getPersonIdentifiers())
            if (response) {
                const personIdentifiers = response
                if (personIdentifiers.length) {
                    personIdentifiers.forEach((identifier) => {
                        this.columnDefs.push({
                            type: 'text',
                            field: identifier.code,
                            headerName: identifier.name,
                            valueGetter: ({ data }) => data.personIdentifiers[identifier.code.toString()]?.identifier
                        })
                    })
                }
            }
        },
        focusIdentifier() {
            this.$nextTick(() => this.$refs.patientIdentifier?.focus())
        },
        async getIdentifiers() {
            const response =
                await this.$_toCallSecure(
                    PatientIdentifierResource.index())
            if (response) {
                this.patientIdentifierOptions = response
                this.identifierSelected = this.globalParameters.mainPatientIdentifierType
            }
        },
        async getCenters() {
            const response =
                await this.$_toCallSecure(
                    CenterResource.getActiveCenters())
            if (response) {
                this.centers = response
                const petitionerDepartment =
                    this.$store.getters.userInformation?.petitioner?.departments
                        ? this.$store.getters.userInformation.petitioner.departments[0]
                        : null
                this.centerSelected = petitionerDepartment?.center ?? response[0]
            }
        },
        setDetailCellRendererParams() {
            this.detailCellRendererParams = {
                detailGridOptions: {
                    pagination: false,
                    rowModelType: AgGridRowModelType.CLIENT_SIDE,
                    floatingFilter: false,
                    sideBar: gridSideBarConfig,
                    getContextMenuItems: (params) => {
                        if (params?.node) {
                            return [
                                {
                                    name: this.$tc('global.export'),
                                    subMenu: [
                                        {
                                            name: this.$tc('global.contextMenu.excel'),
                                            action: () => {
                                                const onlySelected = !!(params.api.getSelectedNodes().length)
                                                params.api.exportDataAsExcel({
                                                    onlySelected,
                                                    processCellCallback: ({ column, value }) =>
                                                        this.$_processCellOutput(column, value)
                                                })
                                            }
                                        }
                                    ]
                                },
                                {
                                    name: this.$tc('clinical.btn.viewReport'),
                                    action: () => this.getRequestReportPDF(params.node.data),
                                    cssClasses: params.node.data.status.code === REQUEST_STATES.COMPLETED.code ? '' : ['not-show']
                                }
                            ]
                        }
                        return []
                    },
                    columnDefs: clinicalSearchPatientModalGridDetailColumnDefs()
                },
                getDetailRowData: async params => {
                    const data = await this.getMasterDetailRowData(params?.node.data.patId)
                    params.successCallback(data)
                }
            }
        },
        async getMasterDetailRowData(patientId) {
            const params = {
                size: PAGINATOR_BIG_PAGE_SIZE
            }
            const response =
                await this.$_toCallSecure(
                    clinicalRequestService.paginator(
                        null,
                        params,
                        `/clinical/laboratories/patients/${patientId}/requests/previous`))
            if (response) {
                return response.content
            }
            return []
        },
        async getRequestReportPDF(request) {
            const getCaseReportPDFParams = {
                laboratoryId: request.laboratory.labId,
                caseId: request.caseStudy?.casId,
                caseLabel: request.caseStudy.casCasenumber,
                print: false,
                hasAssociatedCases: request.caseStudy.hasCaseStudiesAssociated,
                isClinical: true
            }
            await HelperUtils.getCaseReportPDF(getCaseReportPDFParams)
        },
        onRowClicked({ data }) {
            if (this.hasOperationInAnyLaboratoryGlobalMixin(this.requestOperation.CREATE)) {
                this.selectPatientAlt(data.patId)
            }
        },
        onSelectionChanged({ api }) {
            this.selectedRows = api.getSelectedRows()
        },
        getContextMenuItems(params) {
            if (params?.node) {
                if (this.selectedRows.length === 1) {
                    return [
                        {
                            name: this.isReassign
                                ? this.$tc('clinical.btn.reassignPatient')
                                : this.$tc('component.patient.searcher.createRequest'),
                            action: () => this.createRequest(params.node.data.patId),
                            cssClasses: !this.hasOperationInAnyLaboratoryGlobalMixin(this.requestOperation.CREATE) ? ['not-show'] : ''
                        },
                        {
                            name: this.$tc('component.patient.searcher.patientInformation'),
                            action: () => this.openPatientInformation(params.node.data.patId)
                        }
                    ]
                }
            }
            return []
        },
        openPatientInformation(patId) {
            this.patientIdSelected = patId
            this.$nextTick(() => this.$refs.modalPatientCard.show())
        },
        async selectPatientAlt(patId) {
            this.$emit('patientSelected', patId)
            this.hide()
        },
        async createRequest(patId) {
            this.$emit('patientSelected', patId)
            this.hide()
        },
        onGridReady({ api }) {
            this.gridApi = api
            this.gridApi.setColumnDefs(
                this.$_filterPatientColumns(
                    this.columnDefs,
                    'patMiddleName',
                    'patSurname2'))
            api.sizeColumnsToFit()
        },
        hide() {
            this.$refs.bModal.hide()
        },
        async show(isReassign = false) {
            this.isReassign = isReassign
            this.columnDefs = clinicalSearchPatientModalGridMasterColumnDefs()
            this.rowData = []

            this.setDetailCellRendererParams()
            await Promise.all([
                this.getIdentifiers(),
                this.getCenters(),
                this.addPatientIdentifiersColumns()
            ])

            this.$refs.bModal.show()
            this.isModalOpen = true
            this.$nextTick(() => {
                this.$refs.patientIdentifier.focus()
            })
        },
        onHide() {
            this.isModalOpen = false
            this.$_cleanComponentData()
            this.$emit('onHide')
        },
        onOk() {
        },
        validatePatientId(id) {
            if (id) {
                for (let regex of this.regex) {
                    let regexObj = new RegExp('^(?:' + regex.regex + ')$')
                    if (regexObj.test(id.trim())) {
                        return true
                    }
                }
            }
            return false
        },
        async searchPatients() {
            const validId = this.validatePatientId(this.id)
            if (!validId && this.id && this.id.length) {
                this.showErrorModalGlobal(this.$t('component.modalWarning.content'))
            } else if (this.name || this.surname1 || this.surname2 || this.middleName || validId) {
                this.loading = true
                try {
                    await this.loadRowData()
                    this.searchDone = true
                } catch (e) {
                    this.handleError(e)
                } finally {
                    this.loading = false
                }
                if (this.gridApi) this.gridApi.deselectAll()
            }
            this.selectedRows = []
        },
        clearSearch() {
            this.identifierSelected = this.globalParameters.mainPatientIdentifierType
            this.id = ''
            this.name = ''
            this.surname1 = ''
            this.surname2 = ''
            this.middleName = ''
            this.searchDone = false
        },
        async loadRowData() {
            const paginator = await this.loadPatientPaginator()
            if (paginator.totalElements === 0) {
                this.noResults = true
                this.tooManyResults = false
                this.rowData = []
            } else {
                this.rowData = paginator.content
                this.tooManyResults = false
                this.noResults = false
            }
        },
        async loadPatientPaginator() {
            let params = {
                size: PAGINATOR_BIG_PAGE_SIZE,
                id: this.id,
                surname1: this.surname1,
                surname2: this.surname2,
                middleName: this.middleName,
                name: this.name,
                identifierType: this.identifierSelected.code
            }
            if (this.identifierSelected.code === this.centerIdentifierCode) {
                params.centerCode = this.centerSelected.code
            }
            return patientResource.paginator(params)
        },
        handleError(e) {
            if (!e.response) {
                throw e
            }
            if (e.response.data.code === this.$apiExceptions.RESULT_LIMIT_EXCEEDED) {
                this.rowData = []
                this.tooManyResults = true
                this.noResults = false
            }
        },
        async selectPatient(selectedPatient) {
            this.selectedPatient = await patientResource.findById(selectedPatient.patId)
        }
    }
}
</script>
<style lang="scss">
.create-request-modal {
    .loading-state {
        width: 100%;
        height: calc(100vh - 400px);
        display: flex;
        align-items: center;
        justify-content: center;
    }
}
.create-request-modal .id-model-select .multiselect__content-wrapper {
    width: auto;
    min-width: 100px;
}
.create-request-modal .ag-layout-normal {
    padding: 0;
}
.create-request-modal .patient-container .empty-search {
    height: calc(100vh - 400px);
}
.create-request-modal .patient-container .btn__icon .btn__icon--primary {
    margin: 0 3px !important;
}
.create-request-modal .form-control {
    color: vt-color(primary) !important;
}
.create-request-modal .registry-case-wizard .form-control {
    color: vt-color(dark-text) !important;
}
.create-request-modal .search-box input,
.patientFusionModal .search-box input {
    border: none;
    border-radius: 0;
}
.create-request-modal .input-section,
.patientFusionModal .input-section {
    position: relative;
    width: 100%;
    color: vt-color(primary);
}
.create-request-modal .input-section:before,
.patientFusionModal .input-section:before {
    content: '';
    width: 1px;
    height: 15px;
    background: vt-color(general-border);
    z-index: 1;
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    margin: auto;
}
.create-request-modal .search-box div div:nth-last-child(2) .input-section:before,
.patientFusionModal .search-box div div:nth-last-child(2) .input-section:before {
    background: transparent;
}
.create-request-modal .search-box .col,
.patientFusionModal .search-box .col {
    display: flex;
    align-items: center;
}
.create-request-modal .select-section,
.patientFusionModal .select-section {
    position: relative;
    width: 100%;
}
.create-request-modal .select-section .multiselect__tags,
.patientFusionModal .select-section .multiselect__tags {
    border: none;
    display: flex;
    align-items: center;
}
.create-request-modal .select-section .multiselect__tags input,
.patientFusionModal .select-section .multiselect__tags input {
    margin-bottom: 0 !important;
}
.create-request-modal .select-section .multiselect__select,
.patientFusionModal .select-section .multiselect__select {
    padding: 0;
    top: -2px;
}
.create-request-modal .multiselect__placeholder,
.patientFusionModal .multiselect__placeholder {
    color: #495057;
}
.create-request-modal .select-section.identifiers .multiselect__placeholder,
.patientFusionModal .select-section.identifiers .multiselect__placeholder, {
    color: vt-color(primary);
}
.create-request-modal .multiselect,
.patientFusionModal .multiselect {
    color: vt-color(primary);
}
.create-request-modal .registry-case-wizard .multiselect {
    color: vt-color(dark-text);
}
.create-request-modal .icon-column {
    width: 70px;
}
.create-request-modal .modal-dialog {
    max-width: 75%;
}
.create-request-modal .modal-body {
    border-radius: .3rem;
    min-height: calc(100vh - 160px);
}
.create-request-modal .create-patient-modal .modal-body,
.create-request-modal .edit-patient-modal .modal-body {
    height: auto !important;
}
.create-request-modal .patientFusionModal .modal-body {
    height: calc(100vh - 240px);
}
.create-request-modal .create-patient-modal {
    padding: 0 !important;
}
.create-request-modal .vt-tabs .nav-tabs .nav-link,
.vt-tabs--small .nav-tabs .nav-link {
    font-size: font-size(very-small) !important;
}
.create-request-modal .nav-pills {
    padding: 0 !important;
    background: vt-color(light-primary);
    height: 100%;
}
.create-request-modal .tabs.row {
    height: calc(100vh - 360px) !important;
    border: 1px solid rgba(0, 0, 0, 0.125);
}
.create-request-modal .nav-link.active {
    background: vt-color(white-light);
    color: vt-color(primary);
    position: relative;
}
.create-request-modal .nav-link.active::before {
    content: "";
    position: absolute;
    width: 5px;
    height: 100%;
    background: vt-color(primary);
    left: 0;
}
.create-request-modal .nav-link {
    border-radius: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 40px;
    width: 70px;
    border-bottom: 2px solid vt-color(white-light);
}
.create-request-modal .vt-tabs .nav-tabs .nav-link.active,
.vt-tabs--small .nav-tabs .nav-link.active {
    border-bottom: 2px solid vt-color(primary);
}
.create-request-modal .vt-tabs .nav-tabs .nav-link,
.vt-tabs--small .nav-tabs .nav-link {
    border-bottom: 2px solid transparent;
}
.create-request-modal .vt-tabs .nav-tabs .nav-link,
.vt-tabs--small .nav-tabs .nav-link i {
    font-size: font-size(very-normal) !important;
}
.create-request-modal .techniques-tab .nav-link.active::before {
    background: transparent;
}
.create-request-modal .section-indicator {
    position: absolute;
    width: auto;
    left: 23px;
    margin-top: 5px;
}
.create-request-modal .nav-link {
    width: 50px;
}
</style>
<style lang="scss"
       scoped>
.title {
    font-size: font-size(normal);
    color: vt-color(primary);
}
.vertical-line {
    width: 1px;
    background: #dee2e6;
    display: inline-block;
    margin: 0 15px;
    height: 20px;
}
.ps-filter-form {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    max-width: 1200px;
    margin-bottom: 24px;
}
.ps-grid {
    height: 450px;
}
.create-request-modal .ag-status-bar {
    display: none;
}
small {
    color: grey;
}
.error-msg {
    font-size: 1.1rem;
    padding-top: 1rem;
    color: #fc6274;
}
</style>
<style lang="scss">
.create-request-modal .form-group {
    margin-bottom: 0;
}
@media (max-width: 1300px) {
    .create-request-modal .modal-dialog {
        max-width: 95%;
    }
}
.create-request-modal .page_renderer {
    padding: 0 5px !important;
    height: calc(100% - 30px) !important;
    overflow-y: auto !important;
    margin: 10px !important;
}
.create-request-modal .page_renderer .vt-grid.ag-theme-balham.ps-grid {
    height: auto !important;
}
.create-request-modal .ps-grid {
    height: calc(100vh - 290px) !important;
    min-height: 425px;
}
.ps-grid.patient .ag-row.ag-row-no-focus.ag-row-odd.ag-row-level-1.ag-full-width-row.ag-row-position-absolute {
    background-color: #FFF !important;
}

</style>
